import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Form,
  ModalFooter,
  SelectSearch,
} from "@telluscompany/design-system";

import { useLocalization, translateOperationalModule } from "@i18n";

import { UserCombobox } from "../../../../shared";
import { useLicenseInventoryQuery } from "../../../license-inventory/api/license-inventory";
import {
  assignLicenseSchema,
  type AssignLicenseFormData,
} from "../../schemas";

interface AssignLicenseFormProps {
  onSubmit: (data: AssignLicenseFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const INVENTORY_LIMIT = 100;

export const AssignLicenseForm = ({
  onSubmit,
  onCancel,
  isSubmitting,
}: AssignLicenseFormProps) => {
  const { translate } = useLocalization();

  // Carrega o inventário ativo (vigência atual) para popular o select.
  // `availableQuantity` é informativo na UI; o backend continua sendo a
  // autoridade quanto à cota — a validação aqui apenas evita cliques óbvios
  // em linhas sem saldo.
  const { data: inventoryPage, isFetching: isFetchingInventory } =
    useLicenseInventoryQuery({
      page: 1,
      limit: INVENTORY_LIMIT,
      onlyActive: true,
    });

  const inventoryOptions = useMemo(() => {
    const availableLabel = translate(
      "admin.licenseAssignments.form.licenseOption.available",
    );
    return (inventoryPage?.data ?? []).map((item) => ({
      label: `${item.complexName} / ${item.structureName} / ${translateOperationalModule(item.moduleName, translate)} (${availableLabel}: ${item.availableQuantity})`,
      value: item.licenseId,
    }));
  }, [inventoryPage, translate]);

  const form = useForm<AssignLicenseFormData>({
    resolver: zodResolver(assignLicenseSchema(translate)),
    defaultValues: { assigneeUserId: "", licenseId: "" },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="px-[32px] py-[16px] flex flex-col gap-4 min-w-[480px]">
          <UserCombobox
            control={form.control}
            name="assigneeUserId"
            label={translate("admin.licenseAssignments.form.label.user")}
            placeholder={translate(
              "admin.licenseAssignments.form.placeholder.selectUser",
            )}
            clearable
            onlyRoleUser
            excludeTellus
          />
          <SelectSearch
            control={form.control}
            name="licenseId"
            label={translate("admin.licenseAssignments.form.label.license")}
            placeholder={translate(
              "admin.licenseAssignments.form.placeholder.selectLicense",
            )}
            options={inventoryOptions}
            disabled={isFetchingInventory}
            clearable
            notFoundText={translate(
              "admin.licenseAssignments.form.licenseEmpty",
            )}
          />
        </div>
        <ModalFooter
          footer={
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {translate("admin.licenseAssignments.button.assign")}
            </Button>
          }
          closeModal={
            <Button type="button" variant="secondary" onClick={onCancel}>
              {translate("admin.users.button.cancel")}
            </Button>
          }
        />
      </form>
    </Form>
  );
};
